import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { LogoMark } from "./Logo";
import { Download, Check, X, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicEstimate = () => {
  const { token } = useParams();
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responding, setResponding] = useState(false);

  useEffect(() => { fetchEstimate(); }, [token]);

  const fetchEstimate = async () => {
    try {
      const { data } = await axios.get(`${API}/public/estimate/${token}`);
      setEstimate(data);
    } catch (err) {
      setError("Estimate not found or link has expired.");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setResponding(true);
    try {
      await axios.post(`${API}/public/estimate/${token}/accept`);
      setEstimate(prev => ({ ...prev, status: "accepted" }));
    } catch (err) { alert("Failed to accept"); }
    finally { setResponding(false); }
  };

  const handleDecline = async () => {
    setResponding(true);
    try {
      await axios.post(`${API}/public/estimate/${token}/decline`);
      setEstimate(prev => ({ ...prev, status: "declined" }));
    } catch (err) { alert("Failed to decline"); }
    finally { setResponding(false); }
  };

  const formatCurrency = (amount, currency = "USD") =>
    new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount || 0);

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><p className="text-[15px] text-[#6B7280]">Loading estimate...</p></div>;
  if (error || !estimate) return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
      <div className="text-center"><p className="text-[16px] font-semibold text-[#0A0A0A] mb-2">Estimate not found</p><p className="text-[14px] text-[#6B7280]">{error}</p></div>
    </div>
  );

  const isAccepted = estimate.status === "accepted";
  const isDeclined = estimate.status === "declined";
  const isConverted = estimate.status === "converted";
  const canRespond = estimate.status === "sent" || estimate.status === "draft";

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="public-estimate-page">
      {isAccepted && (
        <div className="bg-[#ECFDF5] border-b border-[#10B981] px-6 py-3 flex items-center justify-center gap-2" data-testid="estimate-accepted-banner">
          <Check className="w-4 h-4 text-[#059669]" />
          <span className="text-[14px] font-medium text-[#065F46]">You accepted this estimate</span>
        </div>
      )}
      {isDeclined && (
        <div className="bg-[#FEF2F2] border-b border-[#EF4444] px-6 py-3 flex items-center justify-center gap-2" data-testid="estimate-declined-banner">
          <X className="w-4 h-4 text-[#DC2626]" />
          <span className="text-[14px] font-medium text-[#991B1B]">You declined this estimate</span>
        </div>
      )}

      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={26} />
            <span className="font-mono font-semibold text-[14px] text-[#0A0A0A]">{estimate.estimate_number}</span>
            <span className="text-[11px] font-bold uppercase tracking-wide text-[#6B7280] bg-[#F3F4F6] px-2 py-0.5">Estimate</span>
          </div>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Accept / Decline Bar */}
        {canRespond && (
          <div className="bg-white border border-[#E5E7EB] p-4 sm:p-5 mb-6" data-testid="estimate-response-bar">
            <p className="text-[14px] text-[#0A0A0A] font-semibold mb-3">
              {estimate.user?.business_name || estimate.user?.name} sent you an estimate for {formatCurrency(estimate.total, estimate.currency)}
            </p>
            <div className="flex gap-3">
              <button onClick={handleAccept} disabled={responding} className="flex items-center gap-2 px-5 py-2.5 bg-[#059669] text-white text-[13px] font-bold hover:bg-[#047857] disabled:opacity-50 transition-colors" data-testid="accept-estimate-btn">
                <Check className="w-4 h-4" /> Accept
              </button>
              <button onClick={handleDecline} disabled={responding} className="flex items-center gap-2 px-5 py-2.5 bg-white border border-[#E5E7EB] text-[#6B7280] text-[13px] font-bold hover:border-[#EF4444] hover:text-[#EF4444] disabled:opacity-50 transition-colors" data-testid="decline-estimate-btn">
                <X className="w-4 h-4" /> Decline
              </button>
            </div>
          </div>
        )}

        <div className="bg-white border border-[#E5E7EB] shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-6 sm:p-12">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start gap-6 mb-10">
            <div>
              <h1 className="font-display text-[22px] tracking-[-0.03em] text-[#0A0A0A]">{estimate.user?.business_name || estimate.user?.name}</h1>
              {estimate.user?.email && <p className="text-[13px] text-[#6B7280]">{estimate.user.email}</p>}
              {estimate.user?.business_address && <p className="text-[12px] text-[#6B7280] whitespace-pre-line mt-0.5">{estimate.user.business_address}</p>}
            </div>
            <div className="text-right">
              <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase mb-1">Estimate</p>
              <p className="font-mono font-semibold text-[18px] text-[#0A0A0A]">{estimate.estimate_number}</p>
            </div>
          </div>

          {/* Bill To & Dates */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div>
              <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase mb-2">Prepared for</p>
              {estimate.client && (
                <>
                  <p className="font-semibold text-[16px] text-[#0A0A0A]">{estimate.client.name}</p>
                  {estimate.client.company && <p className="text-[14px] text-[#6B7280]">{estimate.client.company}</p>}
                  <p className="text-[14px] text-[#6B7280]">{estimate.client.email}</p>
                </>
              )}
            </div>
            <div className="md:text-right">
              <div className="inline-block text-left">
                <div className="mb-3">
                  <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Issue date</p>
                  <p className="font-mono text-[14px] text-[#0A0A0A]">{estimate.issue_date}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Valid until</p>
                  <p className="font-mono text-[14px] text-[#0A0A0A]">{estimate.valid_until}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="mb-8 overflow-x-auto">
            <table className="w-full min-w-[400px]">
              <thead><tr className="border-b border-[#E5E7EB]">
                <th className="text-left py-3 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide">Description</th>
                <th className="text-right py-3 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide w-20">Qty</th>
                <th className="text-right py-3 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide w-28">Rate</th>
                <th className="text-right py-3 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide w-28">Amount</th>
              </tr></thead>
              <tbody>
                {estimate.items.map((item, i) => (
                  <tr key={i} className="border-b border-[#E5E7EB]">
                    <td className="py-4 text-[14px] text-[#0A0A0A]">{item.description}</td>
                    <td className="text-right py-4 font-mono text-[14px]">{item.quantity}</td>
                    <td className="text-right py-4 font-mono text-[14px]">{formatCurrency(item.rate, estimate.currency)}</td>
                    <td className="text-right py-4 font-mono font-semibold text-[14px]">{formatCurrency(item.amount, estimate.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-full sm:w-64">
              <div className="flex justify-between py-2"><span className="text-[14px] text-[#6B7280]">Subtotal</span><span className="font-mono text-[14px]">{formatCurrency(estimate.subtotal, estimate.currency)}</span></div>
              {estimate.taxes?.length > 0 ? estimate.taxes.map((t, i) => (
                <div key={i} className="flex justify-between py-2"><span className="text-[14px] text-[#6B7280]">{t.label} ({t.rate}%)</span><span className="font-mono text-[14px]">{formatCurrency(t.amount, estimate.currency)}</span></div>
              )) : estimate.tax_rate > 0 && (
                <div className="flex justify-between py-2"><span className="text-[14px] text-[#6B7280]">Tax ({estimate.tax_rate}%)</span><span className="font-mono text-[14px]">{formatCurrency(estimate.tax_amount, estimate.currency)}</span></div>
              )}
              {estimate.discount > 0 && <div className="flex justify-between py-2"><span className="text-[14px] text-[#6B7280]">Discount</span><span className="font-mono text-[14px]">-{formatCurrency(estimate.discount, estimate.currency)}</span></div>}
              <div className="flex justify-between py-3 border-t border-[#0A0A0A] mt-2">
                <span className="font-semibold text-[15px]">Total</span>
                <span className="font-display text-[24px] tracking-[-0.02em] text-[#0A0A0A]">{formatCurrency(estimate.total, estimate.currency)}</span>
              </div>
            </div>
          </div>

          {estimate.notes && (
            <div className="border-t border-[#E5E7EB] pt-6">
              <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase mb-2">Notes</p>
              <p className="text-[14px] text-[#6B7280] whitespace-pre-wrap">{estimate.notes}</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PublicEstimate;
